/** Surface classes: labels, colors (consistent across map, profile band, legend). */
import { SURFACES, type Analysis, type Surface } from "@/api/schemas";

/** `color` is a CSS variable (use it in styles); `surfaceColor()` resolves it for canvas and map lines.
 * The unpaved classes are ochre-to-earth, paved ones slate (docs/design.md → Data colours). */
export const SURFACE_META: Record<Surface, { label: string; color: string; token: string; desc: string }> = {
  paved: { label: "Paved", color: "var(--d-paved)", token: "--d-paved", desc: "Asphalt / concrete" },
  paved_rough: {
    label: "Rough paved",
    color: "var(--d-paved-rough)",
    token: "--d-paved-rough",
    desc: "Sett, cobbles, paving stones",
  },
  compacted: {
    label: "Compacted",
    color: "var(--d-compacted)",
    token: "--d-compacted",
    desc: "Compacted / fine gravel, good tracks",
  },
  gravel: {
    label: "Gravel",
    color: "var(--d-gravel)",
    token: "--d-gravel",
    desc: "Loose gravel, grade 2–3 tracks",
  },
  dirt: { label: "Dirt", color: "var(--d-dirt)", token: "--d-dirt", desc: "Ground, grass, mud, grade 4–5" },
  trail: { label: "Trail", color: "var(--d-trail)", token: "--d-trail", desc: "Path / singletrack" },
  unknown: { label: "Unknown", color: "var(--d-unknown)", token: "--d-unknown", desc: "No surface data" },
};

const FALLBACK: Record<Surface, string> = {
  paved: "#5b6670",
  paved_rough: "#8d7fb0",
  compacted: "#d4a94e",
  gravel: "#b8862a",
  dirt: "#7a5230",
  trail: "#5d6b2c",
  unknown: "#a7adb2",
};

/** A surface's colour as a concrete value (for Chart.js and Leaflet, which can't use CSS variables). */
export function surfaceColor(s: Surface): string {
  if (typeof window === "undefined") return FALLBACK[s];
  const v = getComputedStyle(document.documentElement).getPropertyValue(SURFACE_META[s].token).trim();
  return v || FALLBACK[s];
}

/** Surface km sorted for display (largest first), dropping zeros. */
export function surfaceBreakdown(
  a: Pick<Analysis, "surface_km" | "distance_km">,
): { surface: Surface; km: number; pct: number }[] {
  if (!a.surface_km) return [];
  const total = a.distance_km || Object.values(a.surface_km).reduce((x, y) => x + y, 0) || 1;
  return SURFACES.map((s) => ({
    surface: s,
    km: a.surface_km?.[s] ?? 0,
    pct: ((a.surface_km?.[s] ?? 0) / total) * 100,
  }))
    .filter((x) => x.km > 0.05)
    .sort((x, y) => y.km - x.km);
}

/** Find the surface at a given km from segment list (binary search). */
export function surfaceAt(segments: Analysis["segments"], km: number): Surface | null {
  if (!segments || segments.length === 0) return null;
  let lo = 0;
  let hi = segments.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const s = segments[mid]!;
    if (km < s.from_km) hi = mid - 1;
    else if (km > s.to_km) lo = mid + 1;
    else return s.surface;
  }
  return null;
}

/**
 * Split route geometry into polylines of uniform surface for the map.
 * Uses cumulative distance along geometry and the analysis segments.
 */
export function colorizeGeometry(
  geometry: [number, number, number][],
  segments: Analysis["segments"],
): { surface: Surface; points: [number, number][] }[] {
  if (geometry.length < 2) return [];
  const out: { surface: Surface; points: [number, number][] }[] = [];
  let cum = 0;
  let prev = geometry[0]!;
  let cur: { surface: Surface; points: [number, number][] } | null = null;
  for (let i = 0; i < geometry.length; i++) {
    const p = geometry[i]!;
    if (i > 0) cum += haversineKm(prev[1], prev[0], p[1], p[0]);
    prev = p;
    const s = surfaceAt(segments, cum) ?? "unknown";
    const ll: [number, number] = [p[1], p[0]];
    if (!cur || cur.surface !== s) {
      const last: [number, number] | undefined = cur ? cur.points[cur.points.length - 1] : undefined;
      cur = { surface: s, points: last ? [last, ll] : [ll] };
      out.push(cur);
    } else cur.points.push(ll);
  }
  return out;
}

export function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

/** Climb categories, easy (4) to hardest (HC): an earth ramp (CSS variables, light and dark). */
export const CLIMB_COLOR: Record<string, string> = {
  HC: "var(--d-cat-hc)",
  "1": "var(--d-cat-1)",
  "2": "var(--d-cat-2)",
  "3": "var(--d-cat-3)",
  "4": "var(--d-cat-4)",
};
